import { create } from 'zustand';
import { CounterSlice, createCounterSlice } from './createCounterSlice';
import { ObjectSlice, createObjectSlice } from './createObjectSlice';
import {
    AvailableDrugSlice,
    createAvailableDrugSlice,
} from './createAvailableDrugSlice';
import { StorageSlice, createStorageSlice } from './createStorageSlice';
import { PatientSlice, createPatientSlice } from './createPatientSlice';
import { createNoteSlice, NoteSlice } from './createNoteSlice';
import { createProfileSlice, ProfileSlice } from './createProfileSlice';
import {
    createTransactionFormSlice,
    TransactionFormSlice,
} from './createTransactionFormSlice';
import {
    createFormOverviewSlice,
    FormOverviewSlice,
} from './createFormOverviewSlice';
import {
    AmbulanceFormSlice,
    createAmbulanceFormSlice,
} from './createConnectedTransactionFormSlice';
import { ReferensSlice, createReferensSlice } from './createReferensSlice';
import { SnackbarMessageSlice, createMessageSlice } from './createMessageSlice';
import {
    TransactionErrorSlice,
    createTransactionErrorSlice,
} from './createTransactionErrorSlice';
import { BarCodeSlice, createBarCodeSlice } from './createBarCodeSlice.ts';
import {
    createDefaultFormRegistrationMethodSlice,
    DefaultFormRegistrationMethod,
} from './createDefaultFormRegistrationSlice.ts';
import zukeeper from 'zukeeper';
import {
    DrugsInStorageSlice,
    createDrugsInStorageSlice,
} from './createDrugsInStorageSlice';
import {
    ShouldBeInventorySlice,
    createShouldBeInventorySlice,
} from './createShouldBeInventorySlice';
import {
    DrugQuantitiesViewSlice,
    createDrugQuantitiesViewSlice,
} from './createDrugQuantitiesViewSlice';
import { createEditModeSlice, EditModeSlice } from './createEditModeSlice.ts';
import {
    createShiftChangeInventorySlice,
    ShiftChangeInventorySlice,
} from './createShiftChangeInventorySlice.ts';
import {
    createInventoryRecordsSlice,
    InventoryRecordsSlice,
} from './createInventoryRecordsSlice.ts';
import { AppStateSlice, createAppStateSlice } from './createAppStateSlice.ts';
import {
    createDisposedCauseSlice,
    DisposedCauseSlice,
} from './createDisposedCauseSlice';
import {
    createDisposedFormSlice,
    DisposedFormSlice,
} from './createDisposedFormSlice';
import { createLoadingSlice, LoadingSlice } from './createLoadingSlice';
import { createUserCacheSlice, UserCacheSlice } from './createUserCacheSlice';
import { createMessagesSlice, MessagesSlice } from './createMessagesSlice';

const useGlobalStore = create<
    CounterSlice &
        ObjectSlice &
        AvailableDrugSlice &
        StorageSlice &
        PatientSlice &
        NoteSlice &
        ProfileSlice &
        TransactionFormSlice &
        FormOverviewSlice &
        AmbulanceFormSlice &
        ReferensSlice &
        SnackbarMessageSlice &
        TransactionErrorSlice &
        BarCodeSlice &
        DefaultFormRegistrationMethod &
        DrugsInStorageSlice &
        ShouldBeInventorySlice &
        DrugQuantitiesViewSlice &
        EditModeSlice &
        ShiftChangeInventorySlice &
        InventoryRecordsSlice &
        AppStateSlice &
        DisposedCauseSlice &
        DisposedFormSlice &
        LoadingSlice &
        UserCacheSlice &
        MessagesSlice
>(
    zukeeper((set) => ({
        ...createCounterSlice(set),
        ...createObjectSlice(set),
        ...createAvailableDrugSlice(set),
        ...createStorageSlice(set),
        ...createPatientSlice(set),
        ...createNoteSlice(set),
        ...createProfileSlice(set),
        ...createTransactionFormSlice(set),
        ...createFormOverviewSlice(set),
        ...createAmbulanceFormSlice(set),
        ...createReferensSlice(set),
        ...createMessageSlice(set),
        ...createTransactionErrorSlice(set),
        ...createBarCodeSlice(set),
        ...createDefaultFormRegistrationMethodSlice(set),
        ...createDrugsInStorageSlice(set),
        ...createShouldBeInventorySlice(set),
        ...createDrugQuantitiesViewSlice(set),
        ...createEditModeSlice(set),
        ...createShiftChangeInventorySlice(set),
        ...createFormOverviewSlice(set),
        ...createInventoryRecordsSlice(set),
        ...createAppStateSlice(set),
        ...createDisposedCauseSlice(set),
        ...createDisposedFormSlice(set),
        ...createLoadingSlice(set),
        ...createUserCacheSlice(set),
        ...createMessagesSlice(set, () => useGlobalStore.getState()),
    }))
);

// is used for chrome plugin zukeeper
if (import.meta.env.MODE === 'development') {
    (window as any).store = useGlobalStore;
}

export { useGlobalStore };
